<template>
  <div class="c-query-editor">
    <Fullscreen
      ref="fullscreen"
      background="#f5f5f5"
      @change="fullscreenChange"
    >
      <div
        class="column no-wrap"
        :class="{ 'absolute-full overflow-hidden': fullscreen }"
      >
        <div class="full-width flex justify-between">
          <span
            v-if="!hideLabel"
            class="bold q-mb-xs block"
          >
            {{ label }}
          </span>
          <div
            v-if="!types.length && !disableCount"
            class="col wrap flex justify-end"
          >
            <span class="text-caption text-error">
              {{ $t("query_editor.no_media_types_selected") }}
            </span>
          </div>
          <div
            v-else-if="showResultCount"
            class="col wrap q-mb-xs flex justify-end"
          >
            <i18n-t
              class="full-width text-right"
              keypath="query_editor.results_found_message"
              scope="global"
              tag="span"
            >
              <template #resultCount>
                <b>
                  <QSpinner v-if="loading" />
                  <template v-else>
                    {{ prettyNumber(resultCount) }}
                  </template>
                </b>
              </template>
              <template #dateRange>
                {{ getDateRangeString() }}
              </template>
            </i18n-t>
            <i18n-t
              v-if="showExclusionsCount"
              class="text-caption text-error"
              keypath="query_editor.results_filtered_message"
              scope="global"
              tag="span"
            >
              <template #resultCount>
                <b>
                  <QSpinner v-if="loading" />
                  <template v-else-if="resultCount !== 'error'">
                    {{ prettyNumber(exclusionsCount) }}
                  </template>
                </b>
              </template>
            </i18n-t>
          </div>
        </div>

        <Codemirror
          ref="editor"
          v-model="editorValue"
          class="full-width col editor"
          :class="{
            error: error || (showError && isEmpty),
            disabled: !$isAdminMode && readOnly && !preview,
            fullscreen,
            'tw:cursor-not-allowed': preview,
          }"
          :extensions="extensions"
          :placeholder="placeholder"
          :style="editorStyle"
          @click="onClick"
          @ready="onReady"
        />
        <div
          v-if="!hideControls"
          class="controls"
        >
          <div class="row justify-end">
            <div>
              <QIcon
                v-if="!fullscreen"
                class="editor-block cursor-pointer"
                name="fullscreen"
                size="20px"
                @click="toggleFullscreen"
              >
                <StreemTooltip>{{
                  $t("query_editor.enter_fullscreen")
                }}</StreemTooltip>
              </QIcon>
            </div>
            <div
              v-show="!fullscreen"
              ref="dragHandle"
              class="resize-handle"
            >
              <QIcon
                class="editor-block"
                name="img:https://assets.streem.com.au/icons/resize.svg"
              />
            </div>
          </div>
        </div>

        <QIcon
          v-if="fullscreen"
          class="absolute-bottom-right editor-block cursor-pointer"
          name="fullscreen_exit"
          size="34px"
          @click="toggleFullscreen"
        >
          <StreemTooltip anchor="top left">{{
            $t("query_editor.exit_fullscreen")
          }}</StreemTooltip>
        </QIcon>
        <span
          v-if="error"
          class="input-error flex-center q-pa-sm flex"
          :class="{ 'editor-block': fullscreen }"
        >
          <QIcon
            class="q-pr-sm"
            name="error"
            size="16px"
          />
          {{ $t("query_editor.error_in_query") }}
          <StreemTooltip>
            {{ error }}
          </StreemTooltip>
        </span>

        <QueryEditorHelpButton
          v-if="!hideHelpButton"
          class="q-mt-sm"
        />
      </div>
    </Fullscreen>
  </div>
</template>

<script>
import { autocompletion, completionKeymap } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import {
  bracketMatching,
  foldKeymap,
  indentOnInput,
} from "@codemirror/language";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { EditorState } from "@codemirror/state";
import {
  crosshairCursor,
  drawSelection,
  dropCursor,
  EditorView,
  keymap,
  lineNumbers,
  rectangularSelection,
} from "@codemirror/view";
import { debounce } from "lodash-es";
import { ref } from "vue";
import { Codemirror } from "vue-codemirror";

import { getLocaleText } from "shared/boot/i18n";
import useResizer from "shared/composables/useResizer";
import DateRange from "shared/helpers/DateRange";
import { getTypesFromSelectedContent } from "shared/helpers/media";
import { prettyNumber } from "shared/helpers/number";

import mode from "./mode";
import QueryEditorHelpButton from "./QueryEditorHelpButton.vue";
import {
  addSyntaxError,
  removeSyntaxError,
  syntaxErrorFieldExtension,
  syntaxErrorMark,
} from "./syntaxErrorMark";
import { theme } from "./theme";

export default {
  name: "QueryEditor",
  components: {
    Codemirror,
    QueryEditorHelpButton,
  },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: () => getLocaleText("query_editor.query_label"),
    },
    brief: {
      type: Object,
      default: () => ({}),
    },
    mediaTypes: {
      type: Array,
      default: null,
    },
    showError: {
      type: Boolean,
    },
    hideControls: {
      type: Boolean,
    },
    readOnly: {
      type: Boolean,
    },
    dateRange: {
      type: Object,
      default: () => DateRange.lastThirtyDays(),
    },
    placeholder: {
      type: String,
      default: "",
    },
    hideLabel: {
      type: Boolean,
    },
    hideHelpButton: {
      type: Boolean,
    },
    hideInstantLoadResults: {
      type: Boolean,
    },
    querySnippets: {
      type: Array,
      default: () => [],
    },
    disableCount: {
      type: Boolean,
    },
    maxHeight: {
      type: Number,
      default: 0,
    },
    minHeight: {
      type: Number,
      default: 200,
    },
    isQuerySnippet: {
      type: Boolean,
    },
    preview: {
      type: Boolean,
    },
  },
  emits: [
    "update:modelValue",
    "has-error",
    "result-count",
    "exclusionCount",
    "update:keywords",
  ],
  setup(props) {
    const editor = ref(null);
    const dragHandle = ref(null);

    const { style, updateMinHeight } = useResizer(editor, dragHandle, {
      axes: ["y"],
      minHeight: props.minHeight,
      maxHeight: props.maxHeight,
    });

    return {
      editor,
      dragHandle,
      style,
      updateMinHeight,
    };
  },
  data() {
    return {
      editorView: null,
      loading: false,
      resultCount: 0,
      exclusionsCount: 0,
      error: false,
      fullscreen: false,
      debouncedRefreshCount: debounce(this.refreshCount, 1600),
    };
  },
  computed: {
    extensions() {
      return [
        lineNumbers(),
        history(),
        drawSelection(),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        autocompletion(),
        rectangularSelection(),
        crosshairCursor(),
        highlightSelectionMatches(),
        bracketMatching(),
        keymap.of([
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...completionKeymap,
        ]),
        EditorView.lineWrapping,
        EditorState.tabSize.of(4),
        EditorState.readOnly.of(this.readOnly),
        EditorState.languageData.of(() => [
          { autocomplete: this.querySnippetAutocompleteSource },
        ]),
        theme,
        mode,
        syntaxErrorFieldExtension,
      ];
    },
    apiClient() {
      return this.$isAdminMode ? this.$streemApiAdmin : this.$streemApiV1;
    },
    isEmpty() {
      return !this.modelValue;
    },
    editorValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    types() {
      if (this.mediaTypes) return this.mediaTypes;

      return getTypesFromSelectedContent(this.brief);
    },
    showExclusionsCount() {
      return /\s*(NOT|UNLESS)\s*/.test(this.editorValue);
    },
    caseSensitive() {
      return this.brief.case_sensitive;
    },
    canLoadCount() {
      if (
        this.disableCount ||
        this.readOnly ||
        !this.types.length ||
        !this.modelValue
      )
        return false;

      return true;
    },
    showResultCount() {
      return (
        !this.hideInstantLoadResults &&
        this.canLoadCount &&
        this.resultCount !== "error"
      );
    },
    editorStyle() {
      if (this.style.height) {
        return this.style;
      }

      return {
        ...(!this.preview ? { height: `${this.minHeight}px` } : {}),
      };
    },
  },
  watch: {
    types: "reload",
    modelValue: "reload",
    caseSensitive: "reload",
    minHeight() {
      this.updateMinHeight(this.minHeight);
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    prettyNumber,
    onReady({ view }) {
      this.editorView = view;
    },
    onClick() {
      this.editorView?.focus();
    },
    toggleFullscreen() {
      this.$refs.fullscreen.toggle();
    },
    fullscreenChange(fullscreen) {
      this.fullscreen = fullscreen;
    },
    querySnippetAutocompleteSource(context) {
      const autocomplete = context.matchBefore(/\{[\w ]*/);

      if (!autocomplete) return null;

      if (autocomplete.from === autocomplete.to && !context.explicit) {
        return null;
      }

      const filter = autocomplete.text.replace("{", "").toLowerCase();

      return {
        from: autocomplete.from,
        filter: false,
        options: this.querySnippetAutocompleteOptions(filter),
      };
    },
    querySnippetAutocompleteOptions(filter) {
      return this.querySnippets
        .filter(
          (snippet) =>
            snippet.group.label.toLowerCase().includes(filter) ||
            snippet.label.toLowerCase().includes(filter)
        )
        .map((snippet) => ({
          label: `${snippet.group.label} - ${snippet.label}`,
          apply: snippet.code,
        }));
    },
    reload() {
      this.clearMarkers();
      this.error = false;
      this.resultCount = 0;

      if (!this.canLoadCount) return;

      this.loading = true;
      this.debouncedRefreshCount();
    },
    async refreshCount() {
      if (!this.canLoadCount) return;

      try {
        await Promise.all([this.loadResultCount(), this.loadExclusionsCount()]);
      } catch (error) {
        let message = this.$t("query_editor.refresh_count_error");

        if (error.response?.data?.error) {
          ({ message } = error.response.data.error);
        }

        this.error = message;

        this.resultCount = "error";
        this.addErrorMarker(error);
        this.$emit("has-error", true);
        this.$emit("result-count", this.resultCount);
      } finally {
        this.loading = false;
      }
    },
    buildParameters() {
      const locationKeys =
        this.brief.filters
          ?.filter((item) => item.target_type === "Location")
          .map((item) => `${item.scope}_${item.target_id}`) ?? [];

      return {
        query: this.modelValue,
        organisation_id: this.brief.organisation_id,
        case_sensitive: Boolean(this.brief.case_sensitive),
        include_international_plus: Boolean(
          this.brief.include_international_plus
        ),
        limit: 0,
        highlight: true,
        types: this.types,
        ...this.dateRange,
        location_keys: locationKeys,
        is_query_snippet: this.isQuerySnippet,
        collapse_syndicated: true,
      };
    },
    loadResultCount() {
      const params = this.buildParameters();
      this.resultCount = 0;

      return this.apiClient
        .post("search/count", { params, handleErrors: false })
        .then((response) => {
          this.resultCount = Number(response.data.count);
          this.$emit("update:keywords", response.data.keywords);
          this.$emit("result-count", this.resultCount);
          this.$emit("has-error", false);
        });
    },
    loadExclusionsCount() {
      if (!this.showExclusionsCount) return Promise.resolve();

      const query = this.modelValue.replace(/\s*NOT\s*/g, " AND ");

      const params = {
        ...this.buildParameters(),
        query,
      };

      this.exclusionsCount = 0;

      return this.apiClient
        .post("search/count", { params, handleErrors: false })
        .then((response) => {
          this.exclusionsCount = Number(response.data.count);
          this.$emit("exclusionCount", this.exclusionsCount);
        })
        .catch(() => {
          // do nothing
        });
    },
    addErrorMarker(error) {
      const { column, line } = error?.response?.data?.error || {};

      if (column && line) {
        const lineInEditor = this.editorView.state.doc.line(
          Math.min(this.editorView.state.doc.lines, line)
        );

        const from = Math.min(
          lineInEditor.from + (column - 1),
          lineInEditor.to - 1
        );

        const to = Math.min(lineInEditor.from + column, lineInEditor.to);

        this.editorView.dispatch({
          effects: addSyntaxError.of(syntaxErrorMark.range(from, to)),
        });
      }
    },
    clearMarkers() {
      this.editorView?.dispatch({
        effects: removeSyntaxError.of(() => false),
      });
    },
    getDateRangeString() {
      if (this.dateRange.range) {
        if (this.dateRange.range.label.includes("Last")) {
          return this.$t("query_editor.date_range_string", {
            dateRange: this.dateRange.toString().toLowerCase(),
          });
        }

        return this.dateRange.toString().toLowerCase();
      }

      return `from ${this.dateRange.toString()}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.c-query-editor {
  position: relative;
  z-index: 1;
}

.editor {
  max-width: 100%;
  flex: 1 1 auto;
  z-index: 0;
  display: block !important; // vue-codemirror sets display: contents inline for some reason

  :deep(.cm-editor) {
    overflow: auto;
    border-radius: 3px;
  }

  &.fullscreen {
    display: contents !important;

    &:deep(.cm-editor) {
      flex: 1;
    }
  }
}

.editor-block {
  color: $ds-denim-2;
}

.resize-handle {
  cursor: row-resize;
  height: 20px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(img) {
    height: 16px;
    width: 16px;
  }
}

.CodeMirror-hint-container {
  :deep(.CodeMirror-hints) {
    position: absolute;
    z-index: 2;
    overflow: hidden;
    cursor: default;
    margin: 0;
    padding: 2px;
    background: white;
    max-height: 50%;
    overflow-y: auto;
    color: #a5a7a3;

    li {
      padding: 10px;
    }
  }
}

.controls {
  position: relative;

  > * {
    position: absolute;
    z-index: 1;
    bottom: 100%;
    right: 0;
  }
}
</style>
